import { BaseRepository } from './base.repository';

export interface AgendamentoItem {
  colaborador_id: number;
  exame_id: number;
  variacao_id: number;
  clinica_id: number;
  data_agendamento: string;
  online?: boolean;
}

export interface CreatePedidoDto {
  agendamentos: AgendamentoItem[];
  tipo_pagamento: 'pix' | 'credito' | 'wallet';
  origem: 'ECOMMERCE' | 'ASSESSORIA';
  empresa_id?: number;
  cnpj?: string;
  solicitante_id?: number;
  client_request_id?: string;
  endereco?: {
    logradouro: string;
    numero: string;
    complemento?: string;
    bairro: string;
    cidade: string;
    estado: string;
    cep: string;
  };
  billing_cnpj?: string;
  billing_razao_social?: string;
  billing_cep?: string;
  billing_logradouro?: string;
  billing_numero?: string;
  billing_complemento?: string;
  billing_bairro?: string;
  billing_municipio?: string;
  billing_uf?: string;
  billing_descricao_servico?: string;
}

export interface PedidoResponse {
  pedido_id: number;
  codigo_pedido: string;
  valor_total: number;
  desconto: number;
  valor_final: number;
  status: string;
  payment_data?: any;
  valor_irrf?: number;
  valor_csll?: number;
  valor_pis?: number;
  valor_cofins?: number;
}

/**
 * Repository para operações relacionadas a pedidos
 */
export class PedidosRepository extends BaseRepository {
  /**
   * Cria um pré-pedido na landing page
   * Requer token válido
   */
  async createPedido(pedidoData: CreatePedidoDto): Promise<PedidoResponse> {
    const response = await this.post<PedidoResponse>(
      'pedidos',
      pedidoData
    );

    return response;
  }

  /**
   * Busca pedido pendente da empresa
   * Requer token válido
   * @param empresaId - ID da empresa
   */
  async getPendingPedido(empresaId?: number): Promise<any | null> {
    try {
      const params = empresaId ? `?empresaId=${empresaId}` : '';
      const response = await this.get<any>(`pedidos/pending${params}`);
      return response;
    } catch (error: any) {
      // Se não houver pedido pendente, retorna null
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Busca detalhes de um pedido por ID
   * Requer token válido
   */
  async getPedidoById(id: number): Promise<any> {
    const response = await this.get<any>(`pedidos/${id}`);
    return response;
  }

  /**
   * Busca detalhes de um pedido por codigo_pedido
   * Endpoint público - não requer autenticação
   */
  async getPedidoByCodigo(codigo: string): Promise<any> {
    const response = await this.get<any>(`pedidos/codigo/${codigo}`);
    return response;
  }

  /**
   * Atualiza um pedido pendente com novos agendamentos
   * Requer token válido
   * @param pedidoId - ID do pedido a ser atualizado
   * @param pedidoData - Novos dados do pedido (agendamentos)
   */
  async updatePedido(pedidoId: number, pedidoData: Partial<CreatePedidoDto>): Promise<PedidoResponse> {
    const response = await this.put<PedidoResponse>(
      `pedidos/${pedidoId}`,
      pedidoData
    );
    return response;
  }

  /**
   * Atualiza apenas o endereço associado a um pedido (PATCH)
   * Requer token válido
   */
  async updatePedidoEndereco(pedidoId: number, enderecoData: any): Promise<any> {
    const response = await this.patch<any>(
      `pedidos/${pedidoId}/endereco`,
      enderecoData
    );
    return response;
  }

  /**
   * Busca colaboradores e exames por pedido_id
   * Requer token válido
   * @param pedidoId - ID do pedido
   */
  async getColaboradoresExamesPorPedido(pedidoId: number): Promise<any> {
    const response = await this.get<any>(`pedidos/colaboradores/${pedidoId}`);
    return response;
  }

  /**
   * Busca pedido por client_request_id
   * Requer token válido
   * @param clientRequestId - UUID gerado no frontend
   */
  async getPedidoByClientRequestId(clientRequestId: string): Promise<any> {
    const response = await this.get<any>(`pedidos/by-client-request/${clientRequestId}`);
    return response;
  }
}

